// OtpViewPage.js
import React, { useState, useEffect, useRef } from 'react';
import {
    Container,
    Paper,
    Typography,
    TextField,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Chip,
    CircularProgress,
    Alert,
    Box,
    InputAdornment,
    IconButton,
    Card,
    CardContent,
    Grid,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
} from '@mui/material';
import {
    Search as SearchIcon,
    Refresh as RefreshIcon,
    Visibility as VisibilityIcon,
    Clear as ClearIcon,
    Phone as PhoneIcon,
    Email as EmailIcon,
    AccessTime as AccessTimeIcon,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import AuthService from '../Auth/AuthService';

// Styled components
const StyledPaper = styled(Paper)(({ theme }) => ({
    padding: theme.spacing(3),
    marginTop: theme.spacing(3),
    borderRadius: theme.spacing(2),
    boxShadow: '0 3px 10px rgba(0,0,0,0.08)',
}));

const SearchPaper = styled(Paper)(({ theme }) => ({
    padding: theme.spacing(2),
    marginBottom: theme.spacing(3),
    backgroundColor: '#f8fafc',
    borderRadius: theme.spacing(2),
}));

const OtpViewPage = () => {
    // State
    const [otpRecords, setOtpRecords] = useState([]);
    const [filteredRecords, setFilteredRecords] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [searchMobile, setSearchMobile] = useState('');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [totalRecords, setTotalRecords] = useState(0);
    const [selectedOtp, setSelectedOtp] = useState(null);
    const [viewDialogOpen, setViewDialogOpen] = useState(false);
    const [statusFilter, setStatusFilter] = useState('all');
    const hasFetched = useRef(false);

    // Fetch all OTP records
    const fetchOtpRecords = async () => {
        setLoading(true);
        setError('');
        try {
            const rest = AuthService.pathUrl("GET", "/otp");
            const response = await AuthService.getAuth(rest);

            if (response && response.length > 0) {
                setOtpRecords(response);
                setFilteredRecords(response);
                setTotalRecords(response.length);
            }
        } catch (err) {
            setError('Failed to fetch OTP records. Please try again.');
            console.error('Error fetching OTP records:', err);
        } finally {
            setLoading(false);
        }
    };

    // Search by mobile number
    const handleSearch = async () => {
        if (!searchMobile.trim()) {
            setFilteredRecords(otpRecords);
            setTotalRecords(otpRecords.length);
            return;
        }

        setLoading(true);
        setError('');
        try {
            const rest = AuthService.pathUrl("GET", `/api/otp/search/mobile/${searchMobile.trim()}`);
            const response = await AuthService.getRequest(rest);

            if (response && response) {
                setFilteredRecords(response);
                setTotalRecords(response.length);
                setPage(0);
            }
        } catch (err) {
            setError('Failed to search OTP records. Please try again.');
            console.error('Error searching OTP records:', err);
            setFilteredRecords([]);
            setTotalRecords(0);
        } finally {
            setLoading(false);
        }
    };

    // Handle search on Enter key
    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            handleSearch();
        }
    };

    // Clear search
    const handleClearSearch = () => {
        setSearchMobile('');
        setFilteredRecords(otpRecords);
        setTotalRecords(otpRecords.length);
        setStatusFilter('all');
        setPage(0);
    };

    // Filter by status
    useEffect(() => {
        let filtered = otpRecords;

        // Apply status filter
        if (statusFilter !== 'all') {
            const isUsed = statusFilter === 'used';
            filtered = filtered.filter(record => record.used === isUsed);
        }

        // Apply mobile search if any
        if (searchMobile.trim()) {
            filtered = filtered.filter(record =>
                record.mobile && record.mobile.includes(searchMobile.trim())
            );
        }

        setFilteredRecords(filtered);
        setTotalRecords(filtered.length);
        setPage(0);
    }, [statusFilter, searchMobile, otpRecords]);

    // Load data on component mount
    useEffect(() => {
        if (hasFetched.current) return;
        hasFetched.current = true;
        fetchOtpRecords();
    }, []);

    // Handle page change
    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    // Handle rows per page change
    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    // Open view dialog
    const handleViewDetails = (record) => {
        setSelectedOtp(record);
        setViewDialogOpen(true);
    };

    // Format date
    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
        });
    };

    // Get status chip color
    const getStatusChipColor = (isUsed) => {
        return isUsed ? 'error' : 'success';
    };

    // Get status label
    const getStatusLabel = (isUsed) => {
        return isUsed ? 'Used' : 'Active';
    };

    return (
        <Container maxWidth="xl">
            <StyledPaper>
                <Typography variant="h4" gutterBottom sx={{ fontWeight: 600, color: '#1a237e' }}>
                    OTP Management
                </Typography>
                <Typography variant="body2" color="textSecondary" gutterBottom sx={{ mb: 3 }}>
                    View and manage all OTP records
                </Typography>

                {/* Search Section */}
                <SearchPaper elevation={0}>
                    <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} md={5}>
                            <TextField
                                fullWidth
                                label="Search by Mobile Number"
                                variant="outlined"
                                value={searchMobile}
                                onChange={(e) => setSearchMobile(e.target.value)}
                                onKeyPress={handleKeyPress}
                                placeholder="Enter mobile number..."
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <PhoneIcon color="action" />
                                        </InputAdornment>
                                    ),
                                    endAdornment: searchMobile && (
                                        <InputAdornment position="end">
                                            <IconButton onClick={handleClearSearch} size="small">
                                                <ClearIcon />
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <FormControl fullWidth>
                                <InputLabel>Status Filter</InputLabel>
                                <Select
                                    value={statusFilter}
                                    onChange={(e) => setStatusFilter(e.target.value)}
                                    label="Status Filter"
                                >
                                    <MenuItem value="all">All Status</MenuItem>
                                    <MenuItem value="active">Active</MenuItem>
                                    <MenuItem value="used">Used</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <Box display="flex" gap={1}>
                                <Button
                                    variant="contained"
                                    onClick={handleSearch}
                                    disabled={loading}
                                    startIcon={loading ? <CircularProgress size={20} /> : <SearchIcon />}
                                    sx={{ flex: 1 }}
                                >
                                    Search
                                </Button>
                                <Button
                                    variant="outlined"
                                    onClick={fetchOtpRecords}
                                    disabled={loading}
                                    startIcon={<RefreshIcon />}
                                >
                                    Refresh
                                </Button>
                            </Box>
                        </Grid>
                    </Grid>
                </SearchPaper>

                {/* Error Alert */}
                {error && (
                    <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
                        {error}
                    </Alert>
                )}

                {/* Summary Cards */}
                <Grid container spacing={2} sx={{ mb: 3 }}>
                    <Grid item xs={12} sm={4}>
                        <Card variant="outlined">
                            <CardContent>
                                <Typography color="textSecondary" gutterBottom>
                                    Total OTPs
                                </Typography>
                                <Typography variant="h4">
                                    {otpRecords.length}
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <Card variant="outlined" sx={{ borderColor: 'success.main' }}>
                            <CardContent>
                                <Typography color="textSecondary" gutterBottom>
                                    Active OTPs
                                </Typography>
                                <Typography variant="h4" color="success.main">
                                    {otpRecords.filter(rec => !rec.used).length}
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <Card variant="outlined" sx={{ borderColor: 'error.main' }}>
                            <CardContent>
                                <Typography color="textSecondary" gutterBottom>
                                    Used OTPs
                                </Typography>
                                <Typography variant="h4" color="error.main">
                                    {otpRecords.filter(rec => rec.used).length}
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>

                {/* Table Section */}
                <TableContainer component={Paper} variant="outlined">
                    <Table>
                        <TableHead sx={{ backgroundColor: '#f5f5f5' }}>
                            <TableRow>
                                <TableCell><strong>ID</strong></TableCell>
                                <TableCell><strong>Mobile</strong></TableCell>
                                <TableCell><strong>Email</strong></TableCell>
                                <TableCell><strong>OTP Code</strong></TableCell>
                                <TableCell><strong>Created At</strong></TableCell>
                                <TableCell><strong>Status</strong></TableCell>
                                <TableCell align="center"><strong>Actions</strong></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                <TableRow>
                                    <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                                        <CircularProgress />
                                        <Typography variant="body2" sx={{ mt: 1 }}>
                                            Loading OTP records...
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ) : filteredRecords.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                                        <Typography variant="body1" color="textSecondary">
                                            No OTP records found
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                filteredRecords
                                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                    .map((record) => (
                                        <TableRow key={record.id} hover>
                                            <TableCell>{record.id}</TableCell>
                                            <TableCell>
                                                <Box display="flex" alignItems="center" gap={1}>
                                                    <PhoneIcon fontSize="small" color="action" />
                                                    {record.mobile || 'N/A'}
                                                </Box>
                                            </TableCell>
                                            <TableCell>
                                                <Box display="flex" alignItems="center" gap={1}>
                                                    <EmailIcon fontSize="small" color="action" />
                                                    {record.email || 'N/A'}
                                                </Box>
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={record.otpCode}
                                                    size="small"
                                                    variant="outlined"
                                                    sx={{ fontFamily: 'monospace', fontWeight: 'bold' }}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Box display="flex" alignItems="center" gap={1}>
                                                    <AccessTimeIcon fontSize="small" color="action" />
                                                    {formatDate(record.createdAt)}
                                                </Box>
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={getStatusLabel(record.used)}
                                                    color={getStatusChipColor(record.used)}
                                                    size="small"
                                                />
                                            </TableCell>
                                            <TableCell align="center">
                                                <IconButton
                                                    color="primary"
                                                    onClick={() => handleViewDetails(record)}
                                                    size="small"
                                                >
                                                    <VisibilityIcon />
                                                </IconButton>
                                            </TableCell>
                                        </TableRow>
                                    ))
                            )}
                        </TableBody>
                    </Table>
                    <TablePagination
                        rowsPerPageOptions={[5, 10, 25, 50]}
                        component="div"
                        count={totalRecords}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                    />
                </TableContainer>
            </StyledPaper>

            {/* View Details Dialog */}
            <Dialog
                open={viewDialogOpen}
                onClose={() => setViewDialogOpen(false)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>
                    <Typography variant="h6">OTP Details</Typography>
                </DialogTitle>
                <DialogContent dividers>
                    {selectedOtp && (
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <Typography variant="subtitle2" color="textSecondary">
                                    ID
                                </Typography>
                                <Typography variant="body1" sx={{ mb: 2 }}>
                                    {selectedOtp.id}
                                </Typography>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Typography variant="subtitle2" color="textSecondary">
                                    Status
                                </Typography>
                                <Chip
                                    label={getStatusLabel(selectedOtp.used)}
                                    color={getStatusChipColor(selectedOtp.used)}
                                    size="small"
                                    sx={{ mt: 0.5 }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Typography variant="subtitle2" color="textSecondary">
                                    Mobile Number
                                </Typography>
                                <Typography variant="body1" sx={{ mb: 2 }}>
                                    {selectedOtp.mobile || 'N/A'}
                                </Typography>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Typography variant="subtitle2" color="textSecondary">
                                    Email
                                </Typography>
                                <Typography variant="body1" sx={{ mb: 2 }}>
                                    {selectedOtp.email || 'N/A'}
                                </Typography>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Typography variant="subtitle2" color="textSecondary">
                                    OTP Code
                                </Typography>
                                <Typography variant="h6" sx={{ fontFamily: 'monospace', color: 'primary.main' }}>
                                    {selectedOtp.otpCode}
                                </Typography>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Typography variant="subtitle2" color="textSecondary">
                                    Created At
                                </Typography>
                                <Typography variant="body1" sx={{ mb: 2 }}>
                                    {formatDate(selectedOtp.createdAt)}
                                </Typography>
                            </Grid>
                            {selectedOtp.used && (
                                <Grid item xs={12}>
                                    <Alert severity="info">
                                        This OTP has been used and is no longer valid.
                                    </Alert>
                                </Grid>
                            )}
                            {!selectedOtp.used && (
                                <Grid item xs={12}>
                                    <Alert severity="success">
                                        This OTP is still active and can be used.
                                    </Alert>
                                </Grid>
                            )}
                        </Grid>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setViewDialogOpen(false)} color="primary">
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
};

export default OtpViewPage;